import type { App } from 'vue';
import ElementPlus, {
    ElCheckbox,
    ElDropdownItem,
    ElIcon,
    ElInput,
    ElMessage,
    ElPopperTrigger,
} from 'element-plus';
import 'element-plus/dist/index.css';
import '@/utils/element-table.ts';

type MessageParams =
    | string
    | {
          message?: string;
          [key: string]: unknown;
      };

type MessageBridge = ((params?: MessageParams) => void) & {
    success: (params?: MessageParams) => void;
    warning: (params?: MessageParams) => void;
    info: (params?: MessageParams) => void;
    error: (params?: MessageParams) => void;
    closeAll: () => void;
};

/**
 * Creates a bridge between Element Plus's ElMessage API and the legacy Vue 2 Options API pattern.
 *
 * This bridge is needed to maintain backward compatibility with existing code that uses
 * `this.$message.success(...)` pattern from the Options API. Without this bridge, migrating
 * to Element Plus would require refactoring all message calls throughout the codebase.
 *
 * The bridge wraps ElMessage and its variants (success, warning, info, error) while ensuring
 * null-safety and providing a consistent API surface that matches the legacy pattern.
 *
 * @returns A callable function with method properties that delegate to ElMessage
 */
const createMessageBridge = (): MessageBridge => {
    return Object.assign(
        (params?: MessageParams) => {
            if (params != null) {
                ElMessage(params as any);
            }
        },
        {
            success: (params?: MessageParams) => {
                if (params != null) {
                    ElMessage.success(params as any);
                }
            },
            warning: (params?: MessageParams) => {
                if (params != null) {
                    ElMessage.warning(params as any);
                }
            },
            info: (params?: MessageParams) => {
                if (params != null) {
                    ElMessage.info(params as any);
                }
            },
            error: (params?: MessageParams) => {
                if (params != null) {
                    ElMessage.error(params as any);
                }
            },
            closeAll: () => {
                ElMessage.closeAll();
            },
        }
    );
};

const applyCompatPatches = () => {
    Object.assign(ElInput, {
        compatConfig: {
            MODE: 3,
            INSTANCE_ATTRS_CLASS_STYLE: false,
        },
    });

    Object.assign(ElIcon, {
        compatConfig: {
            MODE: 3,
            INSTANCE_ATTRS_CLASS_STYLE: false,
        },
    });

    Object.assign(ElPopperTrigger, {
        compatConfig: {
            MODE: 3,
            INSTANCE_ATTRS_CLASS_STYLE: false,
        },
    });

    Object.assign(ElDropdownItem, {
        compatConfig: {
            MODE: 3,
            INSTANCE_ATTRS_CLASS_STYLE: false,
        },
    });

    Object.assign(ElCheckbox, {
        compatConfig: {
            MODE: 3,
        },
    });
};

export default {
    install(app: App) {
        applyCompatPatches();

        // Install Element Plus without locale - locale is handled by ElConfigProvider in App.vue
        // This allows for reactive locale switching at runtime
        app.use(ElementPlus);

        const message = createMessageBridge();

        // Keep compatibility with legacy Options API calls: this.$message.success(...)
        app.config.globalProperties.$message = message;
        app.provide('$message', message);
    },
};

export { applyCompatPatches, createMessageBridge };
